import { hasExactly, intWithin, isPlainRecord, marketRuleSetting, resolveMarketRule } from './market-rule-kit.js';
export const RIDER_NEARBY_VEHICLES_KEY = 'rider.nearbyVehicles';
/** The heading steps a version may choose; `0` draws no heading. */
export const RIDER_NEARBY_VEHICLES_HEADING_STEPS = [0, 30, 45, 60, 90];
export const RIDER_NEARBY_VEHICLES_BOUNDS = {
    maxCount: { min: 1, max: 20 },
    radiusMeters: { min: 200, max: 5_000 },
    cellMeters: { min: 50, max: 1_000 },
    maxFixAgeSeconds: { min: 10, max: 300 },
    refreshSeconds: { min: 5, max: 120 },
};
export const DEFAULT_RIDER_NEARBY_VEHICLES = {
    enabled: true,
    maxCount: 8,
    radiusMeters: 1_500,
    cellMeters: 150,
    maxFixAgeSeconds: 60,
    refreshSeconds: 15,
    headingStepDegrees: 45,
};
/** An unreadable version: nothing is shown (a privacy control is never loosened by a value nobody can read). */
export const STRICT_RIDER_NEARBY_VEHICLES = {
    enabled: false,
    maxCount: RIDER_NEARBY_VEHICLES_BOUNDS.maxCount.min,
    radiusMeters: RIDER_NEARBY_VEHICLES_BOUNDS.radiusMeters.min,
    cellMeters: RIDER_NEARBY_VEHICLES_BOUNDS.cellMeters.max,
    maxFixAgeSeconds: RIDER_NEARBY_VEHICLES_BOUNDS.maxFixAgeSeconds.min,
    refreshSeconds: RIDER_NEARBY_VEHICLES_BOUNDS.refreshSeconds.max,
    headingStepDegrees: 0,
};
export const RIDER_NEARBY_VEHICLES_POLICY_REASON_CODES = ['RIDER_NEARBY_VEHICLES_POLICY_GOVERNED', 'RIDER_NEARBY_VEHICLES_POLICY_DEFAULT', 'RIDER_NEARBY_VEHICLES_POLICY_INVALID'];
const MEMBERS = ['enabled', 'maxCount', 'radiusMeters', 'cellMeters', 'maxFixAgeSeconds', 'refreshSeconds', 'headingStepDegrees'];
/** The stable issue of a value that is not a `rider.nearbyVehicles` value, or undefined when it is one. */
export function riderNearbyVehiclesIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, MEMBERS))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    const b = RIDER_NEARBY_VEHICLES_BOUNDS;
    if (!intWithin(v.maxCount, b.maxCount.min, b.maxCount.max))
        return 'INVALID_MAX_COUNT';
    if (!intWithin(v.radiusMeters, b.radiusMeters.min, b.radiusMeters.max))
        return 'INVALID_RADIUS_METERS';
    if (!intWithin(v.cellMeters, b.cellMeters.min, b.cellMeters.max))
        return 'INVALID_CELL_METERS';
    if (!intWithin(v.maxFixAgeSeconds, b.maxFixAgeSeconds.min, b.maxFixAgeSeconds.max))
        return 'INVALID_MAX_FIX_AGE_SECONDS';
    if (!intWithin(v.refreshSeconds, b.refreshSeconds.min, b.refreshSeconds.max))
        return 'INVALID_REFRESH_SECONDS';
    if (!RIDER_NEARBY_VEHICLES_HEADING_STEPS.includes(v.headingStepDegrees))
        return 'INVALID_HEADING_STEP_DEGREES';
    return undefined;
}
/** The coarser of two heading steps: no heading (0) is the coarsest, then the larger step. */
const coarserHeading = (a, b) => (a === 0 || b === 0 ? 0 : Math.max(a, b));
export const RIDER_NEARBY_VEHICLES_SPEC = {
    key: RIDER_NEARBY_VEHICLES_KEY,
    issue: riderNearbyVehiclesIssue,
    copy: (v) => ({
        enabled: v.enabled,
        maxCount: v.maxCount,
        radiusMeters: v.radiusMeters,
        cellMeters: v.cellMeters,
        maxFixAgeSeconds: v.maxFixAgeSeconds,
        refreshSeconds: v.refreshSeconds,
        headingStepDegrees: v.headingStepDegrees,
    }),
    defaultValue: DEFAULT_RIDER_NEARBY_VEHICLES,
    strictValue: STRICT_RIDER_NEARBY_VEHICLES,
    reasonPrefix: 'RIDER_NEARBY_VEHICLES_POLICY',
    constraintClass: 'privacy',
    // Never more revealing than a layer above.
    tighten: (deciding, enclosing) => ({
        enabled: deciding.enabled && enclosing.enabled,
        maxCount: Math.min(deciding.maxCount, enclosing.maxCount),
        radiusMeters: Math.min(deciding.radiusMeters, enclosing.radiusMeters),
        cellMeters: Math.max(deciding.cellMeters, enclosing.cellMeters),
        maxFixAgeSeconds: Math.min(deciding.maxFixAgeSeconds, enclosing.maxFixAgeSeconds),
        refreshSeconds: Math.max(deciding.refreshSeconds, enclosing.refreshSeconds),
        headingStepDegrees: coarserHeading(deciding.headingStepDegrees, enclosing.headingStepDegrees),
    }),
};
export function riderNearbyVehiclesSetting(input) {
    return marketRuleSetting(RIDER_NEARBY_VEHICLES_SPEC, input);
}
export function resolveRiderNearbyVehicles(settings, marketId, at, regionCode) {
    return resolveMarketRule(RIDER_NEARBY_VEHICLES_SPEC, settings, marketId, at, regionCode);
}
//# sourceMappingURL=rider-nearby-vehicles-policy.js.map